import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  IconButton,
  Grid,
  Stack,
  Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete"; // ✅ your custom axios
import { toast, Toaster } from "react-hot-toast";
import axiosInstance from "../../../Utils/axiosInstance";

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: "ready" | "uploading" | "done" | "error";
}

const FileUpload: React.FC = () => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSelectFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const mapped = Array.from(selectedFiles).map((file) => ({
      id: `${Date.now()}-${file.name}`,
      file,
      progress: 0,
      status: "ready" as const,
    }));
    setFiles(mapped);
  };

  const triggerFileInput = () => inputRef.current?.click();

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleSelectFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Please select a file first.");
      return;
    }

    try {
      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: "uploading", progress: 10 }))
      );

      const formData = new FormData();
      files.forEach((file) => formData.append("file", file.file));

      const response = await axiosInstance.post(
        "/paper-app/upload-file/",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded * 100) / event.total);
              setFiles((prev) =>
                prev.map((f) => ({ ...f, progress: percent }))
              );
            }
          },
        }
      );

      if (response.status === 201) {
        toast.success("✅ File uploaded successfully!");
        setFiles((prev) =>
          prev.map((f) => ({ ...f, progress: 100, status: "done" }))
        );
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      toast.error("❌ Upload failed. Please try again.");
      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: "error", progress: 0 }))
      );
    }
  };

  return (
    <Box
      sx={{
        width: "80%",
        mt: 4,
        marginTop: "180px",
        marginLeft: "auto",
        marginRight: "auto",
        marginBottom: "40px",
      }}
    >
      <Toaster position="top-right" />

      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          background: "linear-gradient(160deg, #e8f1ff 0%, #f4f5f7 100%)",
        }}
      >
        {/* Drop Zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={triggerFileInput}
          sx={{
            border: "2px dashed rgba(100,120,160,0.4)",
            borderRadius: 2,
            py: 6,
            textAlign: "center",
            cursor: "pointer",
            background:
              "linear-gradient(160deg, rgba(99,102,241,0.08), rgba(14,165,233,0.05))",
            transition: "0.3s",
            "&:hover": {
              background:
                "linear-gradient(160deg, rgba(99,102,241,0.12), rgba(14,165,233,0.1))",
              boxShadow: "0 0 6px rgba(99,102,241,0.2)",
            },
          }}
        >
          <UploadFileIcon
            color="primary"
            sx={{ fontSize: 48, mb: 1, opacity: 0.7 }}
          />
          <Typography variant="h6" fontWeight="600">
            Drag & drop files here
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse your files
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supports CSV, Excel, PDF, and images.
          </Typography>

          <input
            type="file"
            ref={inputRef}
            multiple
            accept=".csv,.xls,.xlsx,.pdf,image/*"
            hidden
            onChange={(e) => handleSelectFiles(e.target.files)}
          />
        </Box>

        {/* File List */}
        {files.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              {files.map((f) => (
                <Grid item xs={12} md={6} key={f.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 500 }}
                      >
                        {f.file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(f.file.size / 1024).toFixed(1)} KB
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={f.progress}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 2,
                          bgcolor: "#e5e7eb",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor:
                              f.status === "error" ? "#ef4444" : "#22c55e",
                          },
                        }}
                      />
                    </Box>

                    <Tooltip title="Remove file">
                      <IconButton
                        onClick={() => removeFile(f.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={() => setFiles([])}
            disabled={files.length === 0}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={files.length === 0}
            sx={{
              background: "linear-gradient(90deg, #2563eb 0%, #38bdf8 100%)",
              color: "#fff",
              "&:hover": {
                background: "linear-gradient(90deg, #1d4ed8 0%, #0ea5e9 100%)",
              },
            }}
          >
            Upload
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default FileUpload;
